// @ts-check
// Contrato de resultado compartido por las cuatro traducciones (DEC-107).
//
// ## La decisión que gobierna todo este módulo: traducir entero o no traducir
//
// Una traducción PARCIAL es la peor salida posible. Si el alumno pide pasar su
// diagrama a Python y recibe un Python al que le falta el bucle de en medio,
// se lleva dos ideas falsas: que su diagrama dice lo que ese Python dice, y
// que Python no sabe expresar lo que él quería. Ninguna de las dos es cierta,
// y las dos son difíciles de desaprender.
//
// Por eso todas las funciones de traducción devuelven `Traduccion`: o bien
// `ok: true` con el resultado COMPLETO, o bien `ok: false` con la lista de
// cosas que no ha sabido traducir. Nunca un resultado a medias, nunca un
// comentario del tipo "// aquí iba algo".
//
// ## Por qué los motivos son una LISTA y no un mensaje
//
// Un programa puede tener tres construcciones no soportadas. Enseñar solo la
// primera obliga al alumno a arreglar, reintentar, descubrir la segunda,
// arreglar... Se recogen todas y se enseñan juntas.

/**
 * @typedef {{
 *   descripcion: string,
 *   donde?: string,
 * }} MotivoNoTraducible
 */

/**
 * @template T
 * @typedef {{ ok: true, valor: T } | { ok: false, motivos: MotivoNoTraducible[] }} Traduccion
 */

/**
 * Resultado correcto.
 * @template T
 * @param {T} valor
 * @returns {Traduccion<T>}
 */
export function traduccionOk(valor) {
  return { ok: true, valor };
}

/**
 * Resultado fallido, con uno o más motivos.
 * @template T
 * @param {MotivoNoTraducible[]} motivos
 * @returns {Traduccion<T>}
 */
export function traduccionFallida(motivos) {
  return { ok: false, motivos };
}

/**
 * Acumulador de motivos para recorridos que visitan muchos nodos.
 *
 * Existe para que un traductor pueda seguir recorriendo el programa entero
 * tras encontrar el primer problema y reunir TODOS los motivos antes de
 * rendirse -- ver el docstring del módulo sobre por qué.
 * @returns {{
 *   anotar: (descripcion: string, donde?: string) => void,
 *   motivos: () => MotivoNoTraducible[],
 *   hayProblemas: () => boolean,
 * }}
 */
export function crearRegistroDeMotivos() {
  /** @type {MotivoNoTraducible[]} */
  const motivos = [];
  return {
    anotar: (descripcion, donde) => {
      // Se descartan los duplicados exactos: un bucle con la misma
      // construcción no soportada en diez iteraciones del recorrido produce
      // diez veces el mismo texto, y una lista repetida se lee peor que una
      // corta.
      const yaEsta = motivos.some((m) => m.descripcion === descripcion && m.donde === donde);
      if (!yaEsta) motivos.push({ descripcion, ...(donde === undefined ? {} : { donde }) });
    },
    motivos: () => motivos.slice(),
    hayProblemas: () => motivos.length > 0,
  };
}

/**
 * Une varias líneas con saltos, descartando las vacías del final.
 * @param {string[]} lineas
 * @returns {string}
 */
export function unirLineas(lineas) {
  return lineas.join('\n').replace(/\n+$/, '') + '\n';
}
